import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, type ReactNode } from "react";
import Landing from "./pages/Landing";
import { BrowserRouter, HashRouter, Navigate, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { isNative } from "@/native/platform";
import ScrollManager from "@/components/ScrollManager";
import RouteProgress from "@/components/RouteProgress";
import { UpdateGate } from "@/components/UpdateGate";
import { trackRouteChunk } from "@/lib/routeLoading";

// Kept as a named factory so it can be preloaded (below) through the same
// module request React.lazy uses — the second call is a cache hit, not a
// second download.
const loadDashboard = () => import("./pages/Dashboard");

// Each import is wrapped so the chunk download is visible to <RouteProgress />
// and to NavLink's pendingClassName. The path is the route the chunk backs.
const Dashboard = lazy(() => trackRouteChunk("/dashboard", loadDashboard));
const Auth = lazy(() => trackRouteChunk("/auth", () => import("./pages/Auth")));
const AuthCallback = lazy(() => trackRouteChunk("/auth/callback", () => import("./pages/AuthCallback")));
const Settings = lazy(() => trackRouteChunk("/settings", () => import("./pages/Settings")));
const History = lazy(() => trackRouteChunk("/history", () => import("./pages/History")));
const DeleteAccount = lazy(() => trackRouteChunk("/delete-account", () => import("./pages/DeleteAccount")));
const Privacy = lazy(() => trackRouteChunk("/privacy", () => import("./pages/Privacy")));
const Terms = lazy(() => trackRouteChunk("/terms", () => import("./pages/Terms")));
const Features = lazy(() => trackRouteChunk("/features", () => import("./pages/Features")));
const Methodology = lazy(() => trackRouteChunk("/methodology", () => import("./pages/Methodology")));
const Pricing = lazy(() => trackRouteChunk("/pricing", () => import("./pages/Pricing")));
const Faq = lazy(() => trackRouteChunk("/faq", () => import("./pages/Faq")));
const HowItWorks = lazy(() => trackRouteChunk("/how-it-works", () => import("./pages/HowItWorks")));
const NotFound = lazy(() => trackRouteChunk("*", () => import("./pages/NotFound")));

const queryClient = new QueryClient();

const AppLoading = () => (
  <div className="mobile-viewport relative flex items-center justify-center overflow-hidden bg-[#030806] text-foreground">
    <div className="mc-instant-hero-motion absolute inset-0" aria-hidden="true" />
    <div className="relative h-8 w-8 animate-spin rounded-full border-2 border-[#5ed29c]/80 border-t-transparent shadow-[0_0_36px_rgba(94,210,156,0.35)]" />
  </div>
);

// Dashboard is 122 KB — by a wide margin the only chunk big enough for the
// suspended-transition stall to be perceptible. Warm it during idle time at the
// two moments it is about to be needed: while signed in (every /dashboard link,
// plus the "/" redirect below) and while on an auth screen (sign-in lands
// there). Signed-out marketing visitors never pay for it.
const DashboardPreloader = () => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const likelyNext = (!loading && Boolean(user)) || pathname.startsWith("/auth");

  useEffect(() => {
    if (!likelyNext) return;

    let cancelled = false;
    const run = () => {
      if (!cancelled) void loadDashboard();
    };

    const supportsIdle = typeof window.requestIdleCallback === "function";
    const handle = supportsIdle ? window.requestIdleCallback(run, { timeout: 2000 }) : window.setTimeout(run, 300);

    return () => {
      cancelled = true;
      if (supportsIdle) window.cancelIdleCallback(handle);
      else window.clearTimeout(handle);
    };
  }, [likelyNext]);

  return null;
};

const HomeRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <AppLoading />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Landing />;
};

const AppRoutes = () => (
  <Suspense fallback={<AppLoading />}>
    <Routes>
      <Route path="/" element={<HomeRoute />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/callback/*" element={<AuthCallback />} />
      <Route path="/callback" element={<AuthCallback />} />
      <Route path="/callback/*" element={<AuthCallback />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/history" element={<History />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/features" element={<Features />} />
      <Route path="/methodology" element={<Methodology />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/faq" element={<Faq />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  </Suspense>
);

const NativeAwareRouter = ({ children }: { children: ReactNode }) => {
  const Router = isNative() ? HashRouter : BrowserRouter;
  return <Router>{children}</Router>;
};

const App = () => (
  <UpdateGate>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <RouteProgress />
          <NativeAwareRouter>
            <ScrollManager />
            <DashboardPreloader />
            <AppRoutes />
          </NativeAwareRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </UpdateGate>
);

export default App;
